/* MoRetro Studio V28.5 - Native print orientation + interactive GLB viewer */
window.MTS = window.MTS || {};

(function(MTS){
  "use strict";

  const THREE_VERSION = "0.160.0";
  let modulesPromise = null;
  const glbCache = new Map();

  function modelFiles(project){
    const files = Array.isArray(project?.files) ? project.files : [];
    return files.filter(file=>/\.(stl|3mf|obj|gcode|glb|gltf)$/i.test(String(file.name || "")) && file.url);
  }

  function images(project){
    const files = Array.isArray(project?.files) ? project.files : [];
    return files.filter(file=>MTS.Files?.isImageFile?.(file));
  }

  function extension(file){
    return String(file?.ext || file?.name || "").split(".").pop().toLowerCase();
  }

  async function loadModules(){
    if(modulesPromise) return modulesPromise;

    modulesPromise = Promise.all([
      import("three"),
      import("three/addons/controls/OrbitControls.js"),
      import("three/addons/loaders/STLLoader.js"),
      import("three/addons/loaders/OBJLoader.js"),
      import("three/addons/loaders/3MFLoader.js"),
      import("three/addons/loaders/GCodeLoader.js"),
      import("three/addons/loaders/GLTFLoader.js"),
      import("three/addons/exporters/GLTFExporter.js")
    ]).then(([THREE, controls, stl, obj, threeMf, gcode, gltf, exporter])=>({
      THREE,
      OrbitControls:controls.OrbitControls,
      STLLoader:stl.STLLoader,
      OBJLoader:obj.OBJLoader,
      ThreeMFLoader:threeMf.ThreeMFLoader,
      GCodeLoader:gcode.GCodeLoader,
      GLTFLoader:gltf.GLTFLoader,
      GLTFExporter:exporter.GLTFExporter
    }));

    return modulesPromise;
  }

  function modelObjectFromLoaded(loaded, ext, THREE){
    if(ext === "stl"){
      loaded.computeVertexNormals();
      const material = new THREE.MeshStandardMaterial({
        color:0x7b5cff,
        metalness:0.08,
        roughness:0.58
      });
      return new THREE.Mesh(loaded, material);
    }
    return loaded;
  }

  function preservePrintOrientation(object, ext, THREE){
    if(ext !== "3mf") return object;

    // 3MF uses Z as its vertical print axis; Three.js uses Y.
    const oriented = new THREE.Group();
    oriented.rotation.x = -Math.PI / 2;
    oriented.add(object);
    oriented.updateMatrixWorld(true);
    return oriented;
  }

  async function fetchModelData(url, ext, status){
    const readResponse = async response=>{
      if(!response.ok) throw new Error(`HTTP ${response.status}`);
      return ["obj", "gcode", "gltf"].includes(ext) ? response.text() : response.arrayBuffer();
    };

    try{
      const direct = await fetch(url, {mode:"cors", cache:"no-store"});
      return await readResponse(direct);
    }catch(directError){
      if(status) status.textContent = "Acces Firebase bloque, relais securise...";
      const api = window.MTS?.WorkshopConfig?.API_BASE || "https://bubbly-vibrancy-production.up.railway.app";
      const relayUrl = `${String(api).replace(/\/$/, "")}/api/files/model-proxy?url=${encodeURIComponent(url)}`;
      try{
        const relay = await fetch(relayUrl, {cache:"no-store"});
        return await readResponse(relay);
      }catch(relayError){
        throw new Error(`Telechargement impossible (${relayError.message || directError.message})`);
      }
    }
  }

  function parseModel(loader, data, ext){
    if(typeof loader.parse !== "function") throw new Error("Parseur 3D indisponible");
    try{
      const parsed = loader.parse(data);
      if(!parsed) throw new Error(`Fichier ${ext.toUpperCase()} vide ou invalide`);
      return parsed;
    }catch(error){
      if(ext !== "3mf") throw error;
      return parse3mfFallback(data);
    }
  }

  function elements(parent, localName){
    return Array.from(parent?.getElementsByTagNameNS?.("*", localName) || []);
  }

  function directChildren(parent, localName){
    return Array.from(parent?.children || []).filter(node=>node.localName === localName);
  }

  function transformMatrix(value, THREE){
    const numbers = String(value || "").trim().split(/\s+/).map(Number);
    if(numbers.length !== 12 || numbers.some(number=>!Number.isFinite(number))) return new THREE.Matrix4();
    return new THREE.Matrix4().set(
      numbers[0], numbers[3], numbers[6], numbers[9],
      numbers[1], numbers[4], numbers[7], numbers[10],
      numbers[2], numbers[5], numbers[8], numbers[11],
      0, 0, 0, 1
    );
  }

  async function parse3mfFallback(data){
    const [{unzipSync, strFromU8}, {THREE}] = await Promise.all([
      import("https://cdn.jsdelivr.net/npm/fflate@0.8.2/esm/browser.js"),
      loadModules()
    ]);
    const archive = unzipSync(new Uint8Array(data));
    const modelEntries = Object.entries(archive).filter(([name])=>/\.model$/i.test(name));
    if(!modelEntries.length) throw new Error("3MF sans fichier .model");

    const finalGroup = new THREE.Group();
    let meshCount = 0;

    for(const [, bytes] of modelEntries){
      const xml = new DOMParser().parseFromString(strFromU8(bytes), "application/xml");
      if(xml.querySelector("parsererror")) continue;

      const objectNodes = new Map(elements(xml, "object").map(node=>[node.getAttribute("id"), node]));
      const cache = new Map();

      function buildMesh(meshNode){
        const vertexNodes = elements(meshNode, "vertex");
        const triangleNodes = elements(meshNode, "triangle");
        if(!vertexNodes.length || !triangleNodes.length) return null;

        const vertices = vertexNodes.map(node=>[
          Number(node.getAttribute("x") || 0),
          Number(node.getAttribute("y") || 0),
          Number(node.getAttribute("z") || 0)
        ]);
        const positions = [];
        triangleNodes.forEach(node=>{
          ["v1", "v2", "v3"].forEach(key=>{
            const vertex = vertices[Number(node.getAttribute(key))];
            if(vertex) positions.push(...vertex);
          });
        });
        if(!positions.length) return null;

        const geometry = new THREE.BufferGeometry();
        geometry.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));
        geometry.computeVertexNormals();
        const material = new THREE.MeshStandardMaterial({
          color:0x7b5cff,
          metalness:0.06,
          roughness:0.58,
          side:THREE.DoubleSide
        });
        meshCount++;
        return new THREE.Mesh(geometry, material);
      }

      function buildObject(objectId, stack = new Set()){
        if(cache.has(objectId)) return cache.get(objectId).clone(true);
        if(stack.has(objectId)) return new THREE.Group();
        stack.add(objectId);

        const node = objectNodes.get(String(objectId));
        const group = new THREE.Group();
        if(node){
          const meshNode = directChildren(node, "mesh")[0];
          const mesh = meshNode ? buildMesh(meshNode) : null;
          if(mesh) group.add(mesh);

          const componentsNode = directChildren(node, "components")[0];
          directChildren(componentsNode, "component").forEach(component=>{
            const child = buildObject(component.getAttribute("objectid"), new Set(stack));
            child.applyMatrix4(transformMatrix(component.getAttribute("transform"), THREE));
            group.add(child);
          });
        }

        stack.delete(objectId);
        cache.set(objectId, group);
        return group.clone(true);
      }

      const buildItems = elements(xml, "build").flatMap(build=>directChildren(build, "item"));
      if(buildItems.length){
        buildItems.forEach(item=>{
          const object = buildObject(item.getAttribute("objectid"));
          object.applyMatrix4(transformMatrix(item.getAttribute("transform"), THREE));
          finalGroup.add(object);
        });
      }else{
        objectNodes.forEach((node, id)=>{
          if(directChildren(node, "mesh").length) finalGroup.add(buildObject(id));
        });
      }
    }

    if(!meshCount) throw new Error("Aucun maillage exploitable dans ce 3MF");
    return finalGroup;
  }

  async function sourceObject(data, ext, modules){
    const {THREE, STLLoader, OBJLoader, ThreeMFLoader, GCodeLoader, GLTFLoader} = modules;

    if(ext === "glb" || ext === "gltf"){
      const gltfLoader = new GLTFLoader();
      const gltf = await gltfLoader.parseAsync(data, "");
      return gltf.scene || gltf.scenes?.[0];
    }

    const Loader = ext === "stl"
      ? STLLoader
      : ext === "obj"
        ? OBJLoader
        : ext === "3mf"
          ? ThreeMFLoader
          : GCodeLoader;
    const loader = new Loader();
    const parsed = await parseModel(loader, data, ext);
    return preservePrintOrientation(modelObjectFromLoaded(parsed, ext, THREE), ext, THREE);
  }

  async function exportGlb(object, GLTFExporter){
    const exporter = new GLTFExporter();
    const result = await exporter.parseAsync(object, {
      binary:true,
      onlyVisible:true,
      trs:false,
      maxTextureSize:2048
    });
    if(!(result instanceof ArrayBuffer) || !result.byteLength){
      throw new Error("Conversion GLB vide");
    }
    return result;
  }

  async function convertedGlbObject(url, data, ext, modules, status){
    const {GLTFLoader, GLTFExporter} = modules;

    if(ext === "glb" || ext === "gltf"){
      if(status) status.textContent = "Chargement GLB...";
      return sourceObject(data, ext, modules);
    }

    let glb = glbCache.get(url);
    if(!glb){
      if(status) status.textContent = `Lecture ${ext.toUpperCase()}...`;
      const original = await sourceObject(data, ext, modules);
      if(!original) throw new Error(`Aucun objet dans le fichier ${ext.toUpperCase()}`);

      if(status) status.textContent = "Conversion locale en GLB...";
      glb = await exportGlb(original, GLTFExporter);
      glbCache.set(url, glb);
      disposeObject(original);
    }else if(status){
      status.textContent = "Chargement du GLB en cache...";
    }

    const loader = new GLTFLoader();
    const converted = await loader.parseAsync(glb.slice(0), "");
    const scene = converted.scene || converted.scenes?.[0];
    if(!scene) throw new Error("GLB converti sans scene");
    return scene;
  }

  function setWireframe(object, active){
    object.traverse?.(node=>{
      const materials = Array.isArray(node.material) ? node.material : [node.material];
      materials.filter(Boolean).forEach(material=>{
        if("wireframe" in material) material.wireframe = active;
      });
    });
  }

  function disposeObject(object){
    object.traverse?.(node=>{
      node.geometry?.dispose?.();
      const materials = Array.isArray(node.material) ? node.material : [node.material];
      materials.filter(Boolean).forEach(material=>material.dispose?.());
    });
  }

  async function mountViewer(root){
    if(!root || root.dataset.mounted === "1") return;
    root.dataset.mounted = "1";

    const canvas = root.querySelector("canvas");
    const status = root.querySelector("[data-viewer-status]");
    const url = root.dataset.modelUrl || "";
    const ext = root.dataset.modelExt || "";
    if(!canvas || !url) return;

    try{
      const modules = await loadModules();
      const {THREE, OrbitControls} = modules;
      if(!root.isConnected) return;

      const scene = new THREE.Scene();
      const isLight = document.documentElement.dataset.mtsTheme === "light";
      scene.background = new THREE.Color(isLight ? 0xf3f5f8 : 0x101117);

      const camera = new THREE.PerspectiveCamera(42, 1, 0.01, 100000);
      camera.position.set(3, 2.4, 3);

      const renderer = new THREE.WebGLRenderer({canvas, antialias:true, alpha:false});
      renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
      renderer.outputColorSpace = THREE.SRGBColorSpace;
      renderer.shadowMap.enabled = true;

      const controls = new OrbitControls(camera, renderer.domElement);
      controls.enableDamping = true;
      controls.dampingFactor = 0.07;
      controls.screenSpacePanning = true;

      scene.add(new THREE.HemisphereLight(0xffffff, 0x303040, 2.1));
      const keyLight = new THREE.DirectionalLight(0xffffff, 2.8);
      keyLight.position.set(4, 7, 5);
      keyLight.castShadow = true;
      scene.add(keyLight);
      const rimLight = new THREE.DirectionalLight(0x58d8ff, 1.5);
      rimLight.position.set(-5, 2, -4);
      scene.add(rimLight);

      const grid = new THREE.GridHelper(10, 20, isLight ? 0xaab1bd : 0x566070, isLight ? 0xd7dbe2 : 0x272b35);
      scene.add(grid);

      if(status) status.textContent = `Chargement ${ext.toUpperCase()}...`;

      const data = await fetchModelData(url, ext, status);
      if(data?.byteLength === 0 || data?.length === 0) throw new Error("Fichier vide");
      const object = await convertedGlbObject(url, data, ext, modules, status);
      scene.add(object);

      const box = new THREE.Box3().setFromObject(object);
      const size = box.getSize(new THREE.Vector3());
      const center = box.getCenter(new THREE.Vector3());
      object.position.sub(center);

      const normalizedBox = new THREE.Box3().setFromObject(object);
      const minY = normalizedBox.min.y;
      object.position.y -= minY;

      const maxSize = Math.max(size.x, size.y, size.z, 1);
      grid.scale.setScalar(Math.max(maxSize / 5, 0.2));
      camera.near = Math.max(maxSize / 1000, 0.01);
      camera.far = Math.max(maxSize * 100, 1000);
      camera.position.set(maxSize * 1.65, maxSize * 1.25, maxSize * 1.65);
      camera.updateProjectionMatrix();
      controls.target.set(0, size.y * 0.42, 0);
      controls.update();

      let autoRotate = true;
      let wireframe = false;
      controls.autoRotate = autoRotate;
      controls.autoRotateSpeed = 1.15;

      function resize(){
        const rect = root.getBoundingClientRect();
        const width = Math.max(240, Math.round(rect.width));
        const height = Math.max(240, Math.round(rect.height));
        renderer.setSize(width, height, false);
        camera.aspect = width / height;
        camera.updateProjectionMatrix();
      }

      const resizeObserver = new ResizeObserver(resize);
      resizeObserver.observe(root);
      resize();

      root.querySelector("[data-viewer-reset]")?.addEventListener("click", ()=>{
        camera.position.set(maxSize * 1.65, maxSize * 1.25, maxSize * 1.65);
        controls.target.set(0, size.y * 0.42, 0);
        controls.update();
      });
      root.querySelector("[data-viewer-rotate]")?.addEventListener("click", event=>{
        autoRotate = !autoRotate;
        controls.autoRotate = autoRotate;
        event.currentTarget.classList.toggle("active", autoRotate);
      });
      root.querySelector("[data-viewer-wireframe]")?.addEventListener("click", event=>{
        wireframe = !wireframe;
        setWireframe(object, wireframe);
        event.currentTarget.classList.toggle("active", wireframe);
      });
      root.querySelector("[data-viewer-fullscreen]")?.addEventListener("click", ()=>{
        root.requestFullscreen?.();
      });

      if(status) status.textContent = `GLB pret - rotation et zoom actifs`;

      function animate(){
        if(!root.isConnected){
          resizeObserver.disconnect();
          controls.dispose();
          disposeObject(object);
          renderer.dispose();
          return;
        }
        controls.update();
        renderer.render(scene, camera);
        requestAnimationFrame(animate);
      }
      animate();
    }catch(error){
      console.error("MoRetro Viewer 3D:", error);
      root.classList.add("error");
      if(status) status.textContent = `Apercu 3D indisponible : ${error.message || "fichier incompatible"}`;
    }
  }

  function mountPending(){
    document.querySelectorAll("[data-mts-3d-viewer]:not([data-mounted])").forEach(mountViewer);
  }

  const observer = new MutationObserver(()=>requestAnimationFrame(mountPending));
  observer.observe(document.documentElement, {childList:true, subtree:true});

  function render(project){
    const models = modelFiles(project);
    const mainModel = models[0] || null;
    const img = images(project)[0] || null;

    if(mainModel){
      const ext = extension(mainModel);
      setTimeout(mountPending, 0);
      return `
        <div class="mts-v7-viewer mts-v28-viewer"
          data-mts-3d-viewer
          data-model-url="${MTS.escapeHtml(mainModel.url || "")}"
          data-model-ext="${MTS.escapeHtml(ext)}">
          <canvas aria-label="Apercu 3D ${MTS.escapeHtml(mainModel.name || "modele")}"></canvas>
          <div class="mts-v28-viewer-tools">
            <button type="button" data-viewer-reset title="Recentrer" aria-label="Recentrer">&#8634;</button>
            <button type="button" class="active" data-viewer-rotate title="Rotation automatique" aria-label="Rotation automatique">&#10227;</button>
            <button type="button" data-viewer-wireframe title="Mode filaire" aria-label="Mode filaire">#</button>
            <button type="button" data-viewer-fullscreen title="Plein ecran" aria-label="Plein ecran">&#9974;</button>
          </div>
          <div class="mts-v28-viewer-status" data-viewer-status>Preparation du modele...</div>
          <div class="mts-v7-viewer-name">
            <strong>${MTS.escapeHtml(mainModel.name || "Fichier 3D")}</strong>
            <em>${ext === "glb" ? "GLB" : `${MTS.escapeHtml(ext.toUpperCase())} vers GLB`} interactif</em>
          </div>
        </div>
      `;
    }

    if(img){
      return `
        <div class="mts-v7-viewer mts-v7-viewer-image">
          <img src="${MTS.escapeHtml(img.url)}" alt="${MTS.escapeHtml(img.name || "Apercu")}" loading="lazy">
          <div class="mts-v7-viewer-chip">Image client</div>
        </div>
      `;
    }

    return `
      <div class="mts-v7-viewer">
        <div class="mts-v7-empty-icon">&#9635;</div>
        <div class="mts-v7-viewer-name">
          <strong>Aucun fichier 3D compatible</strong>
          <em>Ajoute un STL, 3MF, OBJ, GCODE ou GLB</em>
        </div>
      </div>
    `;
  }

  MTS.Viewer = {render, modelFiles, images, mountPending, version:THREE_VERSION};
})(window.MTS);
